import { Box, Grid, makeStyles, Typography } from '@material-ui/core'
import { Formik } from 'formik'
import React, { useCallback, useEffect } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'

import { useActionRunner } from 'src/modules/notification'
import { Button, Container } from '../../../../../ui'
import theme from '../../../../../ui/theme'

import { usePurchasing } from '../../../purchasing/hooks'
import { useRecipes } from '../../hooks'
import { DescriptionOutlined as DescriptionIcon } from '@material-ui/icons'

import RecipeSummarySidePanelDetail from '../RecipeSummarySidePanelDetail'
import LinkIngredientsToProductsModal from './LinkIngredientsToProductsModal'

import { ChevronLeft as ChevronLeftIcon } from '@material-ui/icons'
import { formatPrice } from '../../../../../../utils'
import { debounce } from 'lodash'

const useStyles = makeStyles(theme => ({
  body: {
    backgroundColor: theme.palette.fadedPurple.main,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    [theme.breakpoints.up('desktop')]: {
      flexDirection: 'row',
    },
  },
  content: {
    flexGrow: 1,
  },
  inputRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('tablet')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  buttonLink: {
    textDecoration: 'none',
  },
  title: {
    color: theme.palette.orange.main,
    fontSize: '1.25rem',
    fontFamily: 'Gotham',
    lineHeight: 1.1,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('mobile')]: {
      lineHeight: 1.5,
    },
  },
  backLink: {
    textDecoration: 'inherit',
  },
  backButton: {
    textDecoration: 'none',
    marginBottom: theme.spacing(1),
    color: theme.palette.lightGreen.main,
    textTransform: 'none',
  },
}))

const RecipeDetailView = () => {
  const { purchasingEnvironment } = usePurchasing()
  const classes = useStyles()
  const history = useHistory()

  let { id } = useParams()
  const {
    fetchRecipeById,
    recipe,
    setFromRecipeId,
    fromRecipeId,
    resetFromRecipeId,
  } = useRecipes()

  const [fetchData, isFetching] = useActionRunner(
    [() => fetchRecipeById({ id, purchasingEnvironment })],
    {
      initialFetchingState: true,
    }
  )

  // FIXME: The api is very slow pulling back ingredients, so we
  // FIXME: debounce the calls so we don't end up with a bunch of
  // FIXME: calls in a row (potentially causing a race when switching between recipes)
  const debouncedFetchData = useCallback(
    debounce(() => {
      fetchData()
    }, 500)
  )

  useEffect(() => {
    debouncedFetchData()
  }, [])

  useEffect(() => {
    debouncedFetchData()
  }, [id])

  useEffect(() => {
    debouncedFetchData()
  }, [purchasingEnvironment])

  if (isFetching) {
    return null
  }

  const handleSubmit = async (values, { setErrors }) => {
    console.log(values, setErrors)
  }

  if (!recipe) {
    return null
  }

  const getProduct = ingredient => {
    return ingredient.ingredient.agreementProducts &&
      ingredient.ingredient.agreementProducts[0]
      ? ingredient.ingredient.agreementProducts[0].product
      : null
  }

  const isLinked = ingredient => {
    return (
      (ingredient.ingredient.agreementProducts &&
        ingredient.ingredient.agreementProducts.length > 0) ||
      ingredient.ingredient.externalProduct !== null
    )
  }

  const getTotalPrice = ingredient => {
    // if (
    //   ingredient.ingredientType === 'recipe' &&
    //   ingredient.ingredient.ingredientsCost.isComplete === true
    // ) {
    //   // FIXME: Check hoop jump in fetchRecipeById
    //   return (
    //     ingredient.ingredient.ingredientsCost.value *
    //     ingredient.ingredientAmount
    //   )
    // } else {
    //   const product = getProduct(ingredient)
    //   if (product) {
    //     if (product.gramEquivalentWeight && product.price) {
    //       return (
    //         (product.price / product.gramEquivalentWeight) *
    //         ingredient.ingredientAmount
    //       )
    //     }
    //   }
    // }

    // if (ingredient.ingredient.externalProduct) {
    //   return (
    //     ingredient.ingredient.externalProduct.price *
    //     ingredient.ingredientAmount
    //   )
    // }

    return ingredient && ingredient.ingredientSubtotal
      ? ingredient.ingredientSubtotal.value
      : 0
  }

  const renderContains = ingredient => {
    const contains = getProduct(ingredient)
      ? getProduct(ingredient).allergens.contains
      : []
    const mayContain = getProduct(ingredient)
      ? getProduct(ingredient).allergens.mayContain
      : []
    return (
      <>
        {contains.length > 0 ? (
          <Grid item md={12} xs={12} style={{ paddingBottom: 0 }}>
            (<b>Contains</b>: {contains.join(', ')})
          </Grid>
        ) : null}
        {mayContain.length > 0 ? (
          <Grid item md={12} xs={12} style={{ paddingBottom: 0 }}>
            (<b>May Contain</b>: {mayContain.join(', ')})
          </Grid>
        ) : null}
      </>
    )
  }

  return (
    <Container>
      {fromRecipeId ? (
        <Link
          className={classes.backLink}
          onClick={() => resetFromRecipeId()}
          to={`/recipes/detail/${fromRecipeId}`}
        >
          <Button
            className={classes.backButton}
            size="large"
            startIcon={<ChevronLeftIcon />}
            variant="text"
          >
            Back to Recipe
          </Button>
        </Link>
      ) : null}
      <Formik initialValues={{}} onSubmit={handleSubmit} validationSchema={{}}>
        {({ handleSubmit, isSubmitting }) => (
          <>
            <div className={classes.body}>
              <div className={classes.content}>
                <Grid container spacing={2}>
                  <Grid item md={10} xs={6}>
                    <Typography className={classes.title}>
                      {recipe?.name}
                    </Typography>
                  </Grid>
                  <Grid item md={2} xs={6} style={{ textAlign: 'right' }}>
                    <Link
                      className={classes.backLink}
                      to={`/recipes/${id}/edit`}
                    >
                      <Button
                        color="lightGreen"
                        startIcon={<DescriptionIcon />}
                      >
                        Edit
                      </Button>
                    </Link>
                  </Grid>
                </Grid>

                <div style={{ marginTop: theme.spacing(2) }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h4">Ingredients</Typography>
                  </Grid>

                  {console.log(recipe?.recipeIngredients)}
                  {recipe?.recipeIngredients?.map(ingredient => (
                    <>
                      <Grid item md={1} xs={2}>
                        <b>
                          {Math.floor(ingredient.ingredientAmount)}
                          {ingredient.ingredient.unit === 'grams'
                            ? 'g'
                            : ingredient.ingredient.unit}
                        </b>
                      </Grid>
                      <Grid item md={3} xs={6}>
                        {ingredient.ingredient.name}
                        {getTotalPrice(ingredient)
                          ? ` (${formatPrice(getTotalPrice(ingredient))})`
                          : null}
                      </Grid>

                      <Grid item md={2} xs={4}>
                        {isLinked(ingredient) ? (
                          <>
                            <Link
                              className={classes.backLink}
                              onClick={() => {
                                setFromRecipeId(recipe.id)
                                const url =
                                  ingredient.ingredientType === 'recipe'
                                    ? `/recipes/detail/${ingredient.ingredient.id}`
                                    : `/recipes/ingredient/${ingredient.ingredient.id}/edit`
                                history.push(url)
                              }}
                            >
                              <Button size="small" color="lightGreen">
                                {ingredient.ingredientType === 'recipe' ||
                                ingredient.ingredientType === 'recipe_component'
                                  ? 'View Recipe'
                                  : 'Linked'}
                              </Button>
                            </Link>
                          </>
                        ) : ingredient.ingredientType === 'ingredient' ? (
                          <LinkIngredientsToProductsModal
                            environmentId={purchasingEnvironment}
                            ingredientId={ingredient.ingredient.id}
                            onLinked={() => fetchData()}
                            trigger={props => (
                              <Button color="secondary" {...props} size="small">
                                Link
                              </Button>
                            )}
                          />
                        ) : null}
                      </Grid>
                      <Grid item md={6} xs={12}>
                        {renderContains(ingredient)}
                      </Grid>
                    </>
                  ))}
                </Grid>

                <div style={{ marginTop: theme.spacing(2) }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h4">Steps</Typography>
                  </Grid>
                </Grid>
                {recipe.steps.map(step => {
                  return (
                    <Grid container spacing={2}>
                      <Grid item xs={2}>
                        <Box
                          component="img"
                          src={step.photo}
                          style={{ marginTop: '0.3rem', width: '150px' }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        {step.description}
                      </Grid>
                      <Grid item xs={5}>
                        <Grid container spacing={2}>
                          {step.stepJobType && step.stepJobType[0] ? (
                            <Grid item xs={12}>
                              <Typography variant="h5">Labour</Typography>
                              <Typography variant="body2">
                                {step.stepJobType[0].jobType.name}{' '}
                                {step.stepJobType[0].timeInMins
                                  ? `${step.stepJobType[step.stepJobType.length - 1].timeInMins}m`
                                  : null}
                              </Typography>
                            </Grid>
                          ) : null}
                          {step.stepEquipment && step.stepEquipment[0] ? (
                            <Grid item xs={12}>
                              <Typography variant="h5">Equipment</Typography>
                              <Typography variant="body2">
                                {step.stepEquipment[0].equipment.name}{' '}
                                {step.stepEquipment[0].timeInMins
                                  ? `${step.stepEquipment[step.stepEquipment.length - 1].timeInMins}m`
                                  : null}
                              </Typography>
                            </Grid>
                          ) : null}
                        </Grid>
                      </Grid>
                      <div
                        style={{
                          width: '100%',
                          marginTop: '1rem',
                          marginBottom: '1rem',
                        }}
                      />
                    </Grid>
                  )
                })}
              </div>
              <RecipeSummarySidePanelDetail recipe={recipe} />
            </div>
          </>
        )}
      </Formik>
    </Container>
  )
}

export default RecipeDetailView
